import React, { useState } from 'react';
import { Building2, Server, Lock, Eye, EyeOff, AlertCircle, Siren, X, RefreshCw } from 'lucide-react';
import { User, Site } from '../../types';
import { AdidasLogo } from '../shared';
import { supabase } from '../../config/supabase';

interface LoginScreenProps {
    sites: Site[];
    users: User[];
    onLogin: (user: User) => void;
}

export const LoginScreen: React.FC<LoginScreenProps> = ({ sites, users, onLogin }) => {
    const [selectedSiteId, setSelectedSiteId] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    // State for Role Login
    const [selectedRoleUser, setSelectedRoleUser] = useState<User | null>(null);
    const [rolePassword, setRolePassword] = useState('');

    const handleStoreLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        const site = sites.find(s => s.id === selectedSiteId);
        if (!site) {
            setError('Seleccione una tienda.');
            return;
        }

        if (!password || password.length < 3) {
            setError('Ingrese una contraseña válida (mínimo 3 caracteres).');
            return;
        }

        try {
            // Find the real user for this site
            const realUser = users.find(u => u.siteId === site.id && u.role === 'STORE');

            if (!realUser) {
                console.error('No valid STORE user found for site:', site.id);
                setError('Error de configuración: Esta tienda no tiene un usuario asignado en el sistema.');
                return;
            }

            // Authenticate with Supabase Auth using the store email
            const { data, error: authError } = await supabase.auth.signInWithPassword({
                email: realUser.email,
                password: password
            });

            if (authError || !data.user) {
                setError('Contraseña incorrecta. Por favor, verifique e intente nuevamente.');
                return;
            }

            // Successful authentication - proceed with login
            const user: User = {
                ...realUser,
                siteName: site.name
            };
            onLogin(user);
        } catch (err) {
            console.error('Login error:', err);
            setError('Error al iniciar sesión. Por favor, intente nuevamente.');
        }
    };

    const handleRoleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedRoleUser) return;

        if (!rolePassword || rolePassword.length < 3) {
            setError('Ingrese una contraseña válida (mínimo 3 caracteres)');
            return;
        }

        try {
            // Authenticate with Supabase Auth
            const { data, error: authError } = await supabase.auth.signInWithPassword({
                email: selectedRoleUser.email,
                password: rolePassword
            });

            if (authError || !data.user) {
                setError('Contraseña incorrecta. Por favor, verifique e intente nuevamente.');
                return;
            }

            // Successful authentication - proceed with login
            onLogin(selectedRoleUser);
        } catch (err) {
            console.error('Login error:', err);
            setError('Error al iniciar sesión. Por favor, intente nuevamente.');
        }
    };

    const handleResetData = () => {
        if (window.confirm('¿Resetear todos los datos a valores por defecto? Esto eliminará todos los cambios realizados.')) {
            localStorage.clear();
            window.location.reload();
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-black p-4">
            <div className="bg-white rounded-xl shadow-2xl p-8 w-full max-w-4xl flex flex-col md:flex-row overflow-hidden animate-in fade-in slide-in-from-bottom-4">

                {/* Left: Branding */}
                <div className="md:w-5/12 md:pr-8 md:border-r border-gray-100 mb-6 md:mb-0 flex flex-col justify-center items-center text-center">
                    <div className="flex flex-col items-center mb-6">
                        <AdidasLogo className="w-28 h-28 mb-4 text-black" />
                        <div className="text-4xl font-extrabold tracking-tighter uppercase leading-none">adidas</div>
                        <div className="h-1 w-24 bg-black mt-4 mb-4"></div>
                        <div className="text-xs font-bold text-gray-500 leading-tight tracking-[0.2em] uppercase">
                            Hardworks<br />Networks
                        </div>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-4">Portal de Redes</h2>
                    <p className="text-gray-500 text-sm mb-6 leading-relaxed max-w-xs mx-auto">
                        Bienvenido al sistema unificado de gestión de tickets de conectividad e infraestructura.
                    </p>
                    <div className="bg-blue-50 p-4 rounded-lg text-sm text-blue-800 w-full max-w-xs">
                        <div className="flex items-center justify-center font-bold mb-2">
                            <Siren size={16} className="mr-2" />
                            <span>Soporte 24/7</span>
                        </div>
                        <p className="opacity-90">Mesa Central: <span className="font-mono text-lg font-bold block mt-1">+56 2 2999 9999</span></p>
                    </div>
                </div>

                {/* Right: Login Options */}
                <div className="md:w-7/12 md:pl-8 flex flex-col justify-center space-y-8">

                    {/* Section: Store Access */}
                    <div>
                        <div className="flex items-center mb-4">
                            <div className="bg-black text-white p-1.5 rounded mr-3"><Building2 size={16} /></div>
                            <p className="text-sm font-bold text-gray-900 uppercase tracking-wider">Acceso Tiendas / Sitios</p>
                        </div>

                        <form onSubmit={handleStoreLogin} className="bg-gray-50 p-5 rounded-lg border border-gray-200 space-y-3">
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Seleccionar Sitio / Tienda</label>
                                <div className="relative">
                                    <Building2 className="absolute left-3 top-2.5 text-gray-400" size={16} />
                                    <select
                                        className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-black focus:border-black bg-white text-sm"
                                        value={selectedSiteId}
                                        onChange={(e) => setSelectedSiteId(e.target.value)}
                                    >
                                        <option value="">-- Buscar por nombre o código --</option>
                                        {sites.map(site => (
                                            <option key={site.id} value={site.id}>
                                                [{site.code}] {site.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Contraseña</label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-2.5 text-gray-400" size={16} />
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        placeholder="Contraseña del sitio"
                                        className="w-full pl-10 pr-10 py-2 border rounded-lg focus:ring-2 focus:ring-black focus:border-black"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                    />
                                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600">
                                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                    </button>
                                </div>
                            </div>

                            <button
                                type="submit"
                                className="w-full bg-black text-white py-2 rounded-lg text-sm font-bold hover:bg-gray-800 transition shadow-md"
                            >
                                Ingresar al Sitio
                            </button>
                        </form>
                    </div>

                    <div className="flex items-center justify-center">
                        <div className="h-px bg-gray-200 w-full"></div>
                        <span className="px-3 text-gray-400 text-xs font-bold uppercase">Ó</span>
                        <div className="h-px bg-gray-200 w-full"></div>
                    </div>

                    {/* Section: Ops Access */}
                    <div>
                        <div className="flex items-center mb-4">
                            <div className="bg-blue-600 text-white p-1.5 rounded mr-3"><Server size={16} /></div>
                            <p className="text-sm font-bold text-gray-900 uppercase tracking-wider">Acceso Operaciones IT</p>
                        </div>

                        {selectedRoleUser ? (
                            <form onSubmit={handleRoleLogin} className="bg-blue-50 p-4 rounded-lg border border-blue-200 animate-in fade-in slide-in-from-bottom-2">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center">
                                        <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-xs mr-2">{selectedRoleUser.avatar}</div>
                                        <div>
                                            <div className="font-bold text-sm text-gray-900">{selectedRoleUser.name}</div>
                                            <div className="text-xs text-gray-500">{selectedRoleUser.role}</div>
                                        </div>
                                    </div>
                                    <button type="button" onClick={() => { setSelectedRoleUser(null); setRolePassword(''); }} className="text-gray-400 hover:text-black"><X size={16} /></button>
                                </div>
                                <div className="mb-3">
                                    <input
                                        type="password"
                                        placeholder="Contraseña personal..."
                                        className="w-full border rounded-lg p-2 text-sm focus:ring-2 focus:ring-blue-500"
                                        value={rolePassword}
                                        onChange={e => setRolePassword(e.target.value)}
                                        autoFocus
                                    />
                                </div>
                                <button type="submit" className="w-full bg-blue-600 text-white py-2 rounded-lg text-sm font-bold hover:bg-blue-700">Iniciar Sesión</button>
                            </form>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-48 overflow-y-auto">
                                {users.filter(u => u.role !== 'STORE').map(u => (
                                    <button
                                        key={u.id}
                                        onClick={() => { setSelectedRoleUser(u); setError(''); }}
                                        className="group flex items-center p-3 border border-gray-200 rounded-lg hover:border-blue-500 hover:shadow-md transition bg-white text-left"
                                    >
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs mr-3 ${u.role === 'HELPDESK' ? 'bg-purple-100 text-purple-700' : 'bg-orange-100 text-orange-700'}`}>
                                            {u.avatar}
                                        </div>
                                        <div>
                                            <div className="font-bold text-xs text-gray-800">{u.name}</div>
                                            <div className="text-[10px] text-gray-500 uppercase">{u.role}</div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}

                        {error && (
                            <div className="text-red-500 text-xs font-bold flex items-center bg-red-50 p-2 rounded mt-3">
                                <AlertCircle size={14} className="mr-1" /> {error}
                            </div>
                        )}
                    </div>

                    {/* Reset Data Button & Stats */}
                    <div className="pt-4 border-t border-gray-200 flex items-center justify-between">
                        <div className="text-xs text-gray-500">
                            <span className="font-bold">{sites.length}</span> tiendas disponibles
                        </div>
                        <button
                            onClick={handleResetData}
                            className="text-xs text-gray-400 hover:text-red-600 flex items-center transition"
                            title="Resetear datos a valores por defecto"
                        >
                            <RefreshCw size={12} className="mr-1" />
                            Resetear Datos
                        </button>
                    </div>

                </div>
            </div>
        </div>
    );
};
